import type { I18n, MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, Plural, useLingui } from '@lingui/react/macro';
import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { EditOutlined, DeleteOutlined, CheckOutlined, MessageOutlined, MoreOutlined } from '@ant-design/icons';
import { renderMarkdown } from '../lib/markdown';
import { parseFileLinks } from '../lib/file-links';
import { UserAvatar } from './UserAvatar';
import HeartbeatIcon from './HeartbeatIcon';
import { UserName } from './UserName';
import ReactionBar from './ReactionBar';
import ReactionAddButton from './ReactionAddButton';
import EditEditor from './EditEditor';
import FileLink from './FileLink';
import TaskStatusPill from './TaskStatusPill';
import * as api from '../lib/api';
import SystemMessageBubble, { isCapabilityGrantAction } from './SystemMessageBubble';
import { isSystemMessage, isTaskAssignment, isTaskHeartbeat } from '../lib/message-types';
import { useMessageStore, useThreadStore } from '../stores';
import { useTaskStore } from '../stores/useTaskStore';
import { useDismissibleLayer } from '../hooks/useDismiss';
import { useLongPress } from '../hooks/useLongPress';
import CommandResultCard from './CommandResultCard';
import { AttachmentGrid } from './AttachmentGrid';
import { ToolCardMessage } from './ToolCardMessage';
import { trackCommand, getCommandStatus } from '../hooks/useCommandTracking';
import type { Message } from '../types';
import type { ChannelMember } from '../lib/api';
import { isAllowedAttachmentURL } from '../lib/attachment-url';

interface Props {
  message: Message;
  userMap: Map<string, string>;
  members: ChannelMember[];
  memberMap: Map<string, ChannelMember>;
  currentUserId?: string;
  currentUserRole?: string;
  onRetry?: (message: Message) => void;
  onThreadOpen?: (messageId: string) => void;
  isThreadActive?: boolean;
}

export const isAllowedImageContentURL = isAllowedAttachmentURL;

function MessageItem({ message, userMap, members, memberMap, currentUserId, currentUserRole, onRetry, onThreadOpen, isThreadActive }: Props) {
  const { t, i18n } = useLingui();
  const editMessage = useMessageStore(s => s.editMessage);
  // The row owns its own thread lookup: the store is keyed by origin message, so
  // this is an O(1) read that changes identity only when THIS message's thread
  // does. Threading it down as a prop would re-render every row in the channel
  // whenever any one thread's reply count moved.
  const thread = useThreadStore(s => s.byMessage[message.id]);
  const taskSnapshot = useMemo(() => {
    if (message.content_type !== 'task') return null;
    try { return JSON.parse(message.content) as { task_id: string; seq?: number; title: string; status: string; assignee_id?: string }; }
    catch { return null; }
  }, [message.content, message.content_type]);
  const liveTask = useTaskStore(s => taskSnapshot ? s.byTaskId[taskSnapshot.task_id] : undefined);
  const taskData = liveTask ?? (taskSnapshot ? {
    id: taskSnapshot.task_id,
    seq: taskSnapshot.seq ?? 0,
    title: taskSnapshot.title,
    status: taskSnapshot.status as 'open' | 'in_progress' | 'done' | 'cancelled',
    assignee_id: taskSnapshot.assignee_id,
    assignee: undefined,
  } : null);
  const isSystem = isSystemMessage(message);
  const senderName = isSystem ? t`System` : (message.sender_name ?? userMap.get(message.sender_id) ?? t`Unknown`);
  const isOwn = message.sender_id === currentUserId;
  const isAdmin = currentUserRole === 'admin';
  const isDeleted = !!message.deleted_at;
  const time = formatTime(message.created_at, i18n);

  const [editing, setEditing] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<MessageDescriptor | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);

  const deleteConfirmRef = useRef<HTMLDivElement>(null);
  const actionSheetRef = useRef<HTMLDivElement>(null);
  const cancelDelete = useCallback(() => setShowDeleteConfirm(false), []);
  const closeActionSheet = useCallback(() => setMobileActionsOpen(false), []);
  useDismissibleLayer({ active: showDeleteConfirm, within: deleteConfirmRef, onEscape: cancelDelete });
  useDismissibleLayer({ active: mobileActionsOpen, within: actionSheetRef, onEscape: closeActionSheet });

  const commandData = useMemo(() => {
    if (message.content_type !== 'command') return null;
    try { return JSON.parse(message.content) as { command: string; params?: string }; }
    catch { return null; }
  }, [message.content, message.content_type]);

  const [commandStatus, setCommandStatus] = useState(() => getCommandStatus(message.id));

  useEffect(() => {
    if (message.content_type !== 'command') return;
    trackCommand(message.id);
    const interval = setInterval(() => {
      setCommandStatus(getCommandStatus(message.id));
    }, 1000);
    return () => clearInterval(interval);
  }, [message.id, message.content_type]);

  const renderedContent = useMemo(() => {
    if (message.content_type === 'image' || message.content_type === 'command'
      || message.content_type === 'task' || message.content_type === 'tool_card') return null;
    const content = message.localContent?.map(line => line.map(part => typeof part === 'string' ? part : i18n._(part)).join('')).join('\n') ?? message.content;
    return renderMarkdown(content, message.mentions, userMap);
  }, [message.content, message.localContent, message.content_type, message.mentions, userMap, i18n, i18n.locale]);


  const senderUser = memberMap.get(message.sender_id);
  const agentId = senderUser?.role === 'agent' ? message.sender_id : null;

  const startEdit = useCallback(() => {
    setEditError(null);
    setEditing(true);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditError(null);
    setEditing(false);
  }, []);

  const saveEdit = useCallback(async (newContent: string) => {
    const trimmed = newContent.trim();
    if (!trimmed || trimmed === message.content) {
      cancelEdit();
      return;
    }
    if (message.content_type === 'image' && !isAllowedImageContentURL(trimmed)) {
      setEditError(msg`Image links must use http(s):// or a same-origin path`);
      return;
    }
    setEditError(null);
    setEditSaving(true);
    try {
      const updated = await api.editMessage(message.id, trimmed);
      editMessage(message.channel_id, updated);
      setEditing(false);
    } catch {
      // keep editing on failure
    } finally {
      setEditSaving(false);
    }
  }, [message, editMessage, cancelEdit]);

  const handleDelete = useCallback(async () => {
    try {
      await api.deleteMessage(message.id);
    } catch {
      // ignore
    }
    setShowDeleteConfirm(false);
  }, [message.id]);

  const canEdit = isOwn && !isDeleted && !message._pending && !message._failed;
  const canDelete = (isOwn || isAdmin) && !isDeleted && !message._pending && !message._failed;
  // Messages without reactions expose the add action beside edit and delete.
  // ReactionBar already owns that action once reactions exist, so rendering it
  // again would duplicate the control and increase the message row height.
  const hasReactions = (message.reactions?.length ?? 0) > 0;
  const canAddReaction =
    !isDeleted &&
    !message._pending &&
    !message._failed &&
    !hasReactions &&
    !!currentUserId;
  const canOpenThread = !!onThreadOpen && !isDeleted && !message._pending && !message._failed;
  const hasActions = canEdit || canDelete || canAddReaction || canOpenThread;

  const openThread = useCallback(() => {
    onThreadOpen?.(message.id);
  }, [onThreadOpen, message.id]);

  const openActionSheet = useCallback(() => {
    if (hasActions) setMobileActionsOpen(true);
  }, [hasActions]);

  const longPressHandlers = useLongPress(openActionSheet);

  if (isSystem) {
    let parsed: unknown = null;
    if (message.quick_action) {
      try { parsed = JSON.parse(message.quick_action); } catch { parsed = null; }
    }
    const grantAction = isCapabilityGrantAction(parsed) ? parsed : null;
    const fallback = (!grantAction && parsed && typeof parsed === 'object')
      ? (parsed as { kind?: string; label?: string; action?: string })
      : null;
    return (
      <SystemMessageBubble
        bodyHTML={renderedContent!}
        grantAction={grantAction}
        fallback={fallback}
      />
    );
  }

  if (isTaskHeartbeat(message) && !isDeleted) {
    return (
      <div className="message-heartbeat" data-message-type="task_heartbeat">
        <HeartbeatIcon size={16} active={false} />
        <span><Trans>Heartbeat: status update requested</Trans></span>
        <span className="message-heartbeat-time">{time}</span>
      </div>
    );
  }

  // A task assignment is an event about the task, not its author speaking, so
  // it renders as a thread notice with no avatar or byline.
  if (isTaskAssignment(message) && !isDeleted) {
    return (
      <div className="message-task-assignment" data-message-type="task_assignment">
        <div
          className="message-task-assignment-body"
          dangerouslySetInnerHTML={{ __html: renderedContent! }}
        />
        <div className="message-task-assignment-rule" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div data-message-id={message.id} className={`message-item ${isOwn ? 'message-own' : ''} ${isThreadActive ? 'message-thread-active' : ''}`} {...longPressHandlers}>
      <UserAvatar userId={message.sender_id} displayName={senderName} size={36} />
      <div className="message-body">
        <div className="message-header">
          <span className="message-sender"><UserName displayName={senderName} role={senderUser?.role} /></span>
          <span className="message-time">{time}</span>
          {message.edited_at && !isDeleted && (
            <span className="message-edited" title={t`Edited at ${formatTime(message.edited_at, i18n)}`}>
              <Trans>(edited)</Trans>
            </span>
          )}
          {isOwn && (
            <span className="message-delivery-status">
              {message._pending && <span role="img" aria-label={t`Sending…`}>⏳</span>}
              {message._failed && (
                <>
                  <span role="img" aria-label={t`Send failed`}>❌</span>
                  {onRetry && (
                    <button className="retry-btn" onClick={() => onRetry(message)}><Trans>Retry</Trans></button>
                  )}
                </>
              )}
              {!message._pending && !message._failed && <span className="delivery-check" role="img" aria-label={t`Sent`}><CheckOutlined aria-hidden="true" /></span>}
            </span>
          )}
        </div>
        <div className="message-content">
          {isDeleted ? (
            <div className="message-deleted"><Trans>This message was deleted</Trans></div>
          ) : editing ? (
            <>
              <EditEditor
                initialContent={message.content}
                onSave={saveEdit}
                onCancel={cancelEdit}
                disabled={editSaving}
                users={members}
              />
              {editError && (
                <div className="message-edit-error" role="alert">{i18n._(editError)}</div>
              )}
            </>
          ) : message.content_type === 'command' && commandData ? (
            <div className="message-command">
              <div className="message-command-label">⚡ {commandData.command}{commandData.params ? ` ${commandData.params}` : ''}</div>
              {commandStatus && <CommandResultCard status={commandStatus} />}
            </div>
          ) : message.content_type === 'tool_card' ? (
            <ToolCardMessage body={message.content} />
          ) : message.content_type === 'image' ? (
            <ImageContent url={message.content} />
          ) : message.content_type === 'task' && taskData ? (
            <MessageTextWithFileLinks html={renderMarkdown(taskData.title, message.mentions, userMap)} rawContent={taskData.title} agentId={agentId} />
          ) : (
            <MessageTextWithFileLinks html={renderedContent!} rawContent={message.content} agentId={agentId} />
          )}
          {/* Multi-image attachments (Discord-style grid) */}
          {message.attachments && message.attachments.length > 0 && (
            <AttachmentGrid attachments={message.attachments} />
          )}
        </div>
        {!isDeleted && !editing && (
          <ReactionBar
            reactions={message.reactions ?? []}
            channelId={message.channel_id}
            messageId={message.id}
            currentUserId={currentUserId}
            userMap={userMap}
          />
        )}
        {(thread || taskData) && (
          <div className="message-task-thread-row">
            {taskData && (
              <TaskStatusPill
                task={taskData}
                assigneeName={taskData.assignee?.display_name}
              />
            )}
            {thread && onThreadOpen && (
              <div className="thread-preview-bar" onClick={() => onThreadOpen(message.id)}>
                <MessageOutlined className="thread-preview-icon" aria-hidden="true" />
                <span className="thread-preview-count"><Plural value={thread.message_count} one="# reply" other="# replies" /> ›</span>
              </div>
            )}
          </div>
        )}
      </div>
      {!editing && hasActions && (
        <>
          <div className="message-actions">
            {canAddReaction && currentUserId && (
              <ReactionAddButton
                channelId={message.channel_id}
                messageId={message.id}
                currentUserId={currentUserId}
                variant="toolbar-btn"
              />
            )}
            {canOpenThread && (
              <button className="message-action-btn" onClick={openThread} title={t`Thread`} aria-label={t`Thread`}>
                <MessageOutlined aria-hidden="true" />
              </button>
            )}
            {canEdit && (
              <button className="message-action-btn" onClick={startEdit} title={t`Edit`} aria-label={t`Edit`}>
                <EditOutlined aria-hidden="true" />
              </button>
            )}
            {canDelete && (
              <button className="message-action-btn message-action-delete" onClick={() => setShowDeleteConfirm(true)} title={t`Delete`} aria-label={t`Delete`}>
                <DeleteOutlined aria-hidden="true" />
              </button>
            )}
          </div>
          {/* Whether this is visible is the stylesheet's call, not React's: it
              appears only for a pointer that cannot hover the toolbar above. */}
          <button
            className="message-actions-trigger"
            onClick={openActionSheet}
            title={t`Message actions`}
            aria-label={t`Message actions`}
          >
            <MoreOutlined aria-hidden="true" />
          </button>
        </>
      )}
      {showDeleteConfirm && (
        <div ref={deleteConfirmRef} className="message-delete-overlay" onClick={cancelDelete}>
          <div className="message-delete-dialog" onClick={e => e.stopPropagation()}>
            <p><Trans>Delete this message?</Trans></p>
            <div className="message-delete-dialog-actions">
              <button className="btn-cancel" onClick={cancelDelete}><Trans>Cancel</Trans></button>
              <button className="btn-danger" onClick={handleDelete}><Trans>Delete</Trans></button>
            </div>
          </div>
        </div>
      )}
      {mobileActionsOpen && (
        <div ref={actionSheetRef} className="mobile-action-sheet-overlay" onClick={closeActionSheet}>
          <div className="mobile-action-sheet" onClick={e => e.stopPropagation()}>
            {canAddReaction && currentUserId && (
              <ReactionAddButton
                channelId={message.channel_id}
                messageId={message.id}
                currentUserId={currentUserId}
                variant="sheet-row"
              />
            )}
            {canOpenThread && (
              <button className="mobile-action-sheet-btn" onClick={() => { setMobileActionsOpen(false); openThread(); }}>
                <MessageOutlined aria-hidden="true" /> <Trans>Thread</Trans>
              </button>
            )}
            {canEdit && (
              <button className="mobile-action-sheet-btn" onClick={() => { setMobileActionsOpen(false); startEdit(); }}>
                <EditOutlined aria-hidden="true" /> <Trans>Edit</Trans>
              </button>
            )}
            {canDelete && (
              <button className="mobile-action-sheet-btn mobile-action-sheet-danger" onClick={() => { setMobileActionsOpen(false); setShowDeleteConfirm(true); }}>
                <DeleteOutlined aria-hidden="true" /> <Trans>Delete</Trans>
              </button>
            )}
            <button className="mobile-action-sheet-btn mobile-action-sheet-cancel" onClick={closeActionSheet}>
              <Trans>Cancel</Trans>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Memoised because the row now subscribes to its own thread: without this, a
// re-render of the surrounding list would still re-render every row and undo
// the point of the per-row subscription.
export default React.memo(MessageItem);

function MessageTextWithFileLinks({ html, rawContent, agentId }: { html: string; rawContent: string; agentId: string | null }) {
  const segments = parseFileLinks(rawContent);
  const hasPaths = segments.some(s => s.type === 'path');

  if (!hasPaths) {
    return <div className="message-text" dangerouslySetInnerHTML={{ __html: html }} />;
  }

  const paths = segments.filter(s => s.type === 'path').map(s => s.value);
  const escaped = paths.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`(${escaped.join('|')})`);
  const htmlParts = html.split(re);
  const pathSetLower = new Set(paths);

  return (
    <div className="message-text">
      {htmlParts.map((part, i) =>
        pathSetLower.has(part)
          ? <FileLink key={i} path={part} agentId={agentId} />
          : <span key={i} dangerouslySetInnerHTML={{ __html: part }} />
      )}
    </div>
  );
}

function ImageContent({ url }: { url: string }) {
  const { t } = useLingui();
  const [error, setError] = React.useState(false);

  const isSafe = isAllowedImageContentURL(url);
  if (!isSafe) {
    return <span className="message-image-unsafe">{url}</span>;
  }

  if (error) {
    return (
      <div className="image-error">
        <span><Trans>Image failed to load</Trans></span>
        <a href={url} target="_blank" rel="noopener noreferrer" className="image-fallback-link">
          {url}
        </a>
      </div>
    );
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      <img
        src={url}
        alt={t`Uploaded image`}
        className="message-image"
        onError={() => setError(true)}
        loading="lazy"
      />
    </a>
  );
}

function formatTime(ts: number, i18n: I18n): string {
  const date = new Date(ts);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  const timeStr = date.toLocaleTimeString(i18n.locale, { hour: '2-digit', minute: '2-digit' });

  if (isToday) return timeStr;
  if (isYesterday) return i18n._(msg`Yesterday ${timeStr}`);
  return `${date.toLocaleDateString(i18n.locale, { month: 'short', day: 'numeric' })} ${timeStr}`;
}
